import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useState,
  type FC,
} from 'react';
import {
  AccessibilityInfo,
  Platform,
  Pressable,
  TextInput as RNTextInput,
  View,
} from 'react-native';

import { Select } from '@/components/ui/select';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { getIosBoldTextAdjustedFontWeightStyle } from '@/components/ui/text/ios-bold-text-font-weight';
import { STRINGS } from '@/constants/strings';
import { colors } from '@/theme/colors';
import { theme } from '@/theme/theme';

import { Stack } from '@/components/layout';
import { StackLayoutProps } from '@/components/layout/stack/types';
import { createStyles, useScaling } from '@/hooks';
import { resolveFontFamily, TypographyToken } from '@/theme/typography';
import type {
  TextInputContextValue,
  TextInputDividerProps,
  TextInputFieldProps,
  TextInputFlagProps,
  TextInputHelperTextProps,
  TextInputIconProps,
  TextInputLabelProps,
  TextInputPasswordToggleProps,
  TextInputRootProps,
} from './types';

const TextInputContext = createContext<TextInputContextValue | null>(null);

const useTextInputContext = () => {
  const ctx = useContext(TextInputContext);
  if (!ctx) {
    throw new Error('TextInput subcomponents must be used within a <TextInput.Root> component.');
  }
  return ctx;
};

const TextInputLabel: FC<TextInputLabelProps> = ({ children, style, ...rest }) => {
  const styles = useStyles();
  return (
    <Text variant="Font Inter/Subtitle/Regular 11px" style={[styles.label, style]} {...rest}>
      {children}
    </Text>
  );
};

const TextInputHelperText: FC<TextInputHelperTextProps> = ({ children, style, ...rest }) => {
  const { status } = useTextInputContext();
  const styles = useStyles();
  const color = status === 'error' ? colors.Error : colors['Labels/Secondary'];

  return (
    <Text
      variant="Font Inter/Subtitle/Regular 11px"
      style={[styles.helperText, { color }, style]}
      {...rest}>
      {children}
    </Text>
  );
};

const ContentContainer: FC<StackLayoutProps> = ({ children, style, ...rest }) => {
  const { status, disabled } = useTextInputContext();
  const styles = useStyles();
  const borderColor = status === 'error' ? colors.Error : colors['5'];
  const backgroundColor = disabled ? colors['1'] : colors['Backgrounds/Primary'];
  const { scale, isTablet } = useScaling();

  return (
    <Stack
      direction="row"
      align="center"
      borderWidth={scale(1)}
      borderRadius={scale(4)}
      style={[
        isTablet ? styles.containerIpad : styles.containerPhone,
        {
          borderColor,
          backgroundColor,
        },
        style,
      ]}
      {...rest}>
      {children}
    </Stack>
  );
};

const MemoizedHelperText = memo(TextInputHelperText);
const DEFAULT_PHONE_COUNTRY: CountryCode = 'US';

const toFlagEmoji = (countryCode?: CountryCode) => {
  if (!countryCode) {
    return '🏳️';
  }

  return countryCode
    .toUpperCase()
    .split('')
    .map(char => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');
};

const COUNTRY_OPTIONS = getCountries()
  .map(country => ({
    value: country as string,
    label: `${toFlagEmoji(country)} ${country} (+${getCountryCallingCode(country)})`,
  }))
  .sort((a, b) => a.value.localeCompare(b.value));

/** First country per calling code, used to recover a flag from a bare "+<code>" prefix. */
const COUNTRY_BY_CALLING_CODE = new Map<string, CountryCode>();
for (const country of getCountries()) {
  const code = getCountryCallingCode(country);
  if (!COUNTRY_BY_CALLING_CODE.has(code)) {
    COUNTRY_BY_CALLING_CODE.set(code, country);
  }
}

/** Characters reserved for the "+<code>" prefix (shown in the flag dropdown, not the field),
 *  subtracted from a phone field's `maxLength` so prefix + national stays within budget. */
const PHONE_PREFIX_RESERVED = 3;

const getPhonePrefix = (countryCode?: CountryCode) =>
  countryCode ? `+${getCountryCallingCode(countryCode)}` : '';

const stripPhonePrefix = (value: string, prefix: string) => {
  let rest = value.trimStart();
  // Dirty values can carry a calling code more than once ("+84 +84 0395…") or a
  // stale country's code — remove every leading "+<code>" so the national part
  // never contains one.
  while (rest.startsWith('+')) {
    if (prefix && rest.startsWith(prefix)) {
      rest = rest.slice(prefix.length).trimStart();
      continue;
    }
    const next = rest.replace(/^\+\d{1,3}\s*/, '');
    if (next === rest) break;
    rest = next;
  }
  return rest;
};

const TextInputRoot: FC<TextInputRootProps> = ({
  children,
  status = 'default',
  disabled = false,
  style,
  value,
  onChangeText,
  onSubmitEditing,
  onBlur,
  defaultSecureTextEntry,
  secureTextEntry: secureTextEntryProp,
  onSecureTextEntryChange,
  phoneNumber = false,
  phoneDefaultCountry = DEFAULT_PHONE_COUNTRY,
  onCountryCodeChange,
  onFocus,
}) => {
  const isControlledSecure =
    typeof secureTextEntryProp === 'boolean' && typeof onSecureTextEntryChange === 'function';

  const [secureTextEntryInternal, setSecureTextEntryInternal] = useState(
    defaultSecureTextEntry ?? false,
  );

  const usePasswordToggle = defaultSecureTextEntry === true || isControlledSecure;
  const secureTextEntryResolved = isControlledSecure
    ? secureTextEntryProp
    : secureTextEntryInternal;

  const setSecureTextEntry = useCallback(
    (next: boolean) => {
      if (isControlledSecure) {
        onSecureTextEntryChange(next);
        return;
      }
      setSecureTextEntryInternal(next);
    },
    [isControlledSecure, onSecureTextEntryChange],
  );

  const stringValue = typeof value === 'string' ? value : '';
  const [phoneCountryCode, setPhoneCountryCode] = useState<CountryCode | undefined>(() => {
    if (!phoneNumber) return undefined;

    // On the initial render only: fall back to `phoneDefaultCountry` when we can't parse.
    const parsed = parsePhoneNumberFromString(stringValue, phoneDefaultCountry);
    return parsed?.country ?? phoneDefaultCountry;
  });

  const handleCountryCodeChange = useEffectEvent((countryCode: CountryCode | undefined) => {
    onCountryCodeChange?.(countryCode);
  });

  useEffect(() => {
    if (phoneNumber) {
      handleCountryCodeChange(phoneCountryCode);
    }
  }, [phoneCountryCode, phoneNumber]);

  useEffect(() => {
    if (!phoneNumber) {
      setPhoneCountryCode(undefined);
      return;
    }

    if (!stringValue.startsWith('+')) return;

    const currentCode = phoneCountryCode ? getCountryCallingCode(phoneCountryCode) : undefined;

    // Full parse first — it disambiguates shared calling codes (+1 → US/CA/…).
    const parsed = parsePhoneNumberFromString(stringValue);
    if (parsed?.country) {
      if (getCountryCallingCode(parsed.country) !== currentCode) {
        setPhoneCountryCode(parsed.country);
      }
      return;
    }

    // Values that keep the national trunk zero (e.g. "+84 0395…") don't parse —
    // match the bare calling code so the flag still follows the prefix.
    const digits = stringValue.replace(/\D/g, '');
    for (let len = 3; len >= 1; len--) {
      const code = digits.slice(0, len);
      if (code.length < len) continue;
      if (code === currentCode) return;
      if (code === getCountryCallingCode(phoneDefaultCountry)) {
        setPhoneCountryCode(phoneDefaultCountry);
        return;
      }
      const match = COUNTRY_BY_CALLING_CODE.get(code);
      if (match) {
        setPhoneCountryCode(match);
        return;
      }
    }
  }, [phoneNumber, stringValue, phoneCountryCode, phoneDefaultCountry]);

  const phonePrefix = phoneNumber ? getPhonePrefix(phoneCountryCode) : '';
  const nationalValue = phoneNumber ? stripPhonePrefix(stringValue, phonePrefix) : stringValue;

  const handleChangeText = useCallback<NonNullable<TextInputRootProps['onChangeText']>>(
    nextValue => {
      if (!onChangeText) return;

      if (!phoneNumber) {
        onChangeText(nextValue);
        return;
      }

      // The field only ever holds the national part; strip any prefix/`+` the text may
      // carry (paste, autofill) so a calling code can never end up duplicated.
      const stripped = stripPhonePrefix(nextValue, phonePrefix).replace(/\+/g, '');
      if (!stripped) {
        onChangeText('', phoneCountryCode);
        return;
      }

      const isDeleting = nextValue.length < nationalValue.length;
      const national = isDeleting
        ? stripped
        : new AsYouType(phoneCountryCode).input(stripped.replace(/\D/g, ''));

      onChangeText(phonePrefix ? `${phonePrefix} ${national}` : national, phoneCountryCode);
    },
    [onChangeText, phoneNumber, phonePrefix, phoneCountryCode, nationalValue],
  );

  const handleSelectCountry = useCallback(
    (nextCountry: CountryCode) => {
      setPhoneCountryCode(nextCountry);
      if (!onChangeText) return;
      // Keep the digits (incl. a leading trunk 0), reformat for the new country, swap prefix.
      const digits = nationalValue.replace(/\D/g, '');
      const nextPrefix = getPhonePrefix(nextCountry);
      const national = digits ? new AsYouType(nextCountry).input(digits) : '';
      onChangeText(national ? `${nextPrefix} ${national}` : '', nextCountry);
    },
    [onChangeText, nationalValue],
  );

  return (
    <TextInputContext.Provider
      value={{
        status,
        disabled,
        phoneNumber,
        phoneDefaultCountry,
        phoneCountryCode,
        setPhoneCountryCode: handleSelectCountry,
        value: phoneNumber ? nationalValue : value,
        onChangeText: handleChangeText,
        onBlur,
        onSubmitEditing,
        onFocus,
        ...(usePasswordToggle && {
          secureTextEntry: secureTextEntryResolved,
          setSecureTextEntry,
        }),
      }}>
      <Stack direction="column" width="100%" justify="center" style={style}>
        {children}
      </Stack>
    </TextInputContext.Provider>
  );
};

type RNTextInputInstance = React.ElementRef<typeof RNTextInput>;

function assignTextInputRef(
  ref: React.Ref<RNTextInputInstance> | undefined,
  node: RNTextInputInstance | null,
) {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(node);
    return;
  }
  try {
    (ref as React.MutableRefObject<RNTextInputInstance | null>).current = node;
  } catch {
    // ignore invalid ref objects
  }
}

const TextInputField: FC<TextInputFieldProps> = ({
  variant,
  style,
  editable = true,
  secureTextEntry: secureTextEntryProp,
  multiline,
  numberOfLines,
  trimOnBlur,
  ...rest
}) => {
  const {
    ref: refFromProps,
    value: _discardValue,
    onChangeText: _discardOnChangeText,
    ...fieldRest
  } = rest as typeof rest & {
    ref?: React.Ref<RNTextInputInstance>;
    value?: any;
    onChangeText?: any;
  };
  const _multiline = multiline ?? false;
  const _numberOfLines = numberOfLines ?? (multiline ? undefined : 1);
  const {
    disabled,
    phoneNumber,
    phoneCountryCode,
    value,
    onChangeText,
    onSubmitEditing,
    secureTextEntry: secureTextEntryContext,
    onBlur,
    onFocus,
  } = useTextInputContext();
  const styles = useStyles();
  const isEditable = !disabled && editable;
  const secureTextEntry =
    secureTextEntryProp !== undefined ? secureTextEntryProp : (secureTextEntryContext ?? false);

  const { scale, isTablet } = useScaling();
  const [iosBoldTextEnabled, setIosBoldTextEnabled] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  /**
   * Android (Fabric): static `selection` / setNativeProps often does not apply for pre-filled
   * single-line inputs. RN issue #44171 — drive `selection` from state and clear it on focus.
   */
  const [androidSelection, setAndroidSelection] = useState<
    { start: number; end?: number } | undefined
  >(undefined);

  const setInputRef = useCallback(
    (node: RNTextInputInstance | null) => {
      assignTextInputRef(refFromProps, node);
    },
    [refFromProps],
  );

  useLayoutEffect(() => {
    if (Platform.OS !== 'android') return;
    if (isFocused || _multiline) {
      setAndroidSelection(undefined);
      return;
    }
    if (typeof value !== 'string' || value.length === 0) {
      setAndroidSelection(undefined);
      return;
    }
    // Clear then pin so Fabric reapplies scroll/caret after value updates (e.g. form.reset).
    setAndroidSelection(undefined);
    queueMicrotask(() => {
      setAndroidSelection({ start: 0, end: 0 });
    });
    const pin = () => setAndroidSelection({ start: 0, end: 0 });
    const t0 = setTimeout(pin, 0);
    const t1 = setTimeout(pin, 32);
    const t2 = setTimeout(pin, 100);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [value, isFocused, _multiline]);

  const typographyKey =
    variant ??
    (isTablet
      ? ('Font Inter/Body/Regular 16px' as TypographyToken)
      : ('Font Inter/Subtitle/Regular 12px' as TypographyToken));

  const typo = theme.typography[typographyKey];
  // Resolve the per-face font name (e.g. 'OpenSans-Regular') the same way <Text> does — the raw
  // token family ('OpenSans') matches no registered font, so without this the field falls back to
  // the system font. When the family resolves to a specific weighted face (Open Sans on every
  // platform; Inter/Montserrat on Android/web), drop fontWeight: the weight is baked into the face
  // name, and a separate fontWeight makes iOS collapse every weight to one thickness.
  const { fontFamily: resolvedFontFamily } = resolveFontFamily({
    fontFamily: typo.fontFamily,
    fontWeight: typo.fontWeight,
    fontStyle: 'fontStyle' in typo ? typo.fontStyle : 'normal',
  });
  const isExactFace = resolvedFontFamily !== typo.fontFamily;
  const scaledTypo = {
    ...typo,
    fontFamily: resolvedFontFamily,
    fontSize: scale(typo.fontSize),
    lineHeight: Platform.OS !== 'ios' ? scale(typo.lineHeight) : undefined,
    ...(isExactFace ? { fontWeight: undefined } : null),
  };

  const iosBoldTextInputStyle = useMemo(
    () =>
      iosBoldTextEnabled ? getIosBoldTextAdjustedFontWeightStyle(scaledTypo.fontWeight) : undefined,
    [iosBoldTextEnabled, scaledTypo.fontWeight],
  );

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      setIosBoldTextEnabled(false);
      return;
    }

    const sync = (enabled: boolean) => {
      setIosBoldTextEnabled(enabled);
    };

    let cancelled = false;
    (async () => {
      const enabled = await AccessibilityInfo.isBoldTextEnabled();
      if (!cancelled) sync(enabled);
    })();

    const subscription = AccessibilityInfo.addEventListener('boldTextChanged', sync);
    return () => {
      cancelled = true;
      subscription.remove();
    };
  }, []);

  // In phone mode the field holds only the national part (the prefix lives in the flag
  // dropdown), so the caller's `maxLength` — meant as the total "prefix + number" budget —
  // is reduced by a fixed reserve for the "+<code>" prefix. Non-phone fields keep the
  // caller's `maxLength` untouched.
  const { maxLength: rawMaxLength, ...fieldRestNoMaxLength } = fieldRest as typeof fieldRest & {
    maxLength?: number;
  };
  const effectiveMaxLength =
    phoneNumber && typeof rawMaxLength === 'number'
      ? Math.max(0, rawMaxLength - PHONE_PREFIX_RESERVED)
      : rawMaxLength;

  const inputElement = (
    <RNTextInput
      ref={setInputRef}
      allowFontScaling={false}
      value={value}
      multiline={_multiline}
      numberOfLines={_numberOfLines}
      {...fieldRestNoMaxLength}
      maxLength={effectiveMaxLength}
      keyboardType={
        phoneNumber
          ? (fieldRestNoMaxLength.keyboardType ?? 'name-phone-pad')
          : fieldRestNoMaxLength.keyboardType
      }
      editable={isEditable}
      secureTextEntry={secureTextEntry}
      style={[scaledTypo, iosBoldTextInputStyle, styles.input, styles.inputText, style]}
      placeholderTextColor={fieldRest.placeholderTextColor ?? colors[5]}
      onChangeText={onChangeText}
      onSubmitEditing={onSubmitEditing}
      // When long text is pre-filled (controlled input), RN often keeps the caret at the end and
      // horizontally scrolls so the beginning isn't visible. For edit forms we want to show
      // the beginning by default, while still letting users place the caret normally on focus.
      // iOS: static `selection` when blurred works. Android (Fabric): controlled `androidSelection`.
      selection={
        Platform.OS === 'android'
          ? androidSelection
          : Platform.OS === 'ios' &&
              !isFocused &&
              !_multiline &&
              typeof value === 'string' &&
              value.length > 0
            ? { start: 0, end: 0 }
            : undefined
      }
      onBlur={e => {
        setIsFocused(false);
        if (trimOnBlur && typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed !== value) {
            onChangeText?.(trimmed);
          }
        }
        onBlur?.(e);
      }}
      onFocus={e => {
        setIsFocused(true);
        if (Platform.OS === 'android') {
          setAndroidSelection(undefined);
        }
        onFocus?.(e);
      }}
    />
  );

  if (!phoneNumber) {
    return inputElement;
  }

  const phonePrefix = getPhonePrefix(phoneCountryCode);

  return (
    <View style={styles.phoneField}>
      {phonePrefix ? (
        // Use the input's resolved font (scaledTypo), not `variant`, so the prefix renders
        // with the exact same face/size/weight as the typed number (avoids the raw-family
        // vs resolved-face size mismatch).
        <Text style={[scaledTypo, iosBoldTextInputStyle, styles.phonePrefix]}>{phonePrefix}</Text>
      ) : null}
      {inputElement}
    </View>
  );
};

const TextInputIcon: FC<TextInputIconProps> = ({
  position = 'left',
  size,
  color,
  styleWrapper,
  ...iconProps
}) => {
  const { disabled } = useTextInputContext();
  const styles = useStyles();
  const { scale } = useScaling();
  const baseSize = size ?? scale(24);
  const resolvedColor = disabled ? colors['5'] : (color ?? colors['Blue 2']);

  return (
    <View
      style={[
        styles.iconWrapper,
        position === 'left' ? styles.iconLeft : styles.iconRight,
        styleWrapper,
      ]}>
      <SvgIcon size={baseSize} color={resolvedColor} {...iconProps} />
    </View>
  );
};

const TextInputFlag: FC<TextInputFlagProps> = ({
  position = 'left',
  styleWrapper,
  variant,
  sheetTitle,
}) => {
  const styles = useStyles();
  const { scale } = useScaling();
  const { phoneNumber, phoneCountryCode, setPhoneCountryCode, disabled } = useTextInputContext();

  if (!phoneNumber) {
    return null;
  }

  return (
    <View
      style={[
        styles.flagWrapper,
        position === 'left' ? styles.iconLeft : styles.iconRight,
        styleWrapper,
      ]}>
      <Select
        options={COUNTRY_OPTIONS}
        value={phoneCountryCode ?? null}
        onValueChange={next => setPhoneCountryCode?.(next as CountryCode)}
        disabled={disabled}>
        <Select.Trigger style={styles.flagTrigger} hideIcon accessibilityLabel="Select country">
          <Text variant={variant ?? 'Font Inter/Body/Regular 16px'}>
            {toFlagEmoji(phoneCountryCode)}
          </Text>
          <Stack style={styles.flagChevron}>
            <SvgIcon name="IcPaginationArrow" size={scale(12)} color={colors['6']} />
          </Stack>
        </Select.Trigger>
        <Select.SheetContent
          title={sheetTitle ?? STRINGS.common.countryPickerTitle}
          dropdownMinWidth={scale(220)}
        />
      </Select>
    </View>
  );
};

const TextInputDivider: FC<TextInputDividerProps> = ({ style }) => {
  const styles = useStyles();
  return <View style={[styles.divider, style]} />;
};

const TextInputPasswordToggle: FC<TextInputPasswordToggleProps> = ({
  size,
  color,
  styleWrapper,
  iconName,
  hiddenIconName,
}) => {
  const styles = useStyles();
  const { scale } = useScaling();
  const { disabled, secureTextEntry, setSecureTextEntry } = useTextInputContext();
  const baseSize = size ?? scale(24);
  const resolvedColor = disabled ? colors['5'] : (color ?? colors['Blue 2']);

  if (setSecureTextEntry == null) {
    return null;
  }

  const isHidden = secureTextEntry === true;

  return (
    <Pressable
      onPress={() => setSecureTextEntry(!isHidden)}
      style={({ pressed }) => [
        styles.iconWrapper,
        styles.iconRight,
        styles.passwordToggle,
        styleWrapper,
        pressed && styles.passwordTogglePressed,
      ]}
      accessibilityRole="button"
      accessibilityLabel={isHidden ? 'Show password' : 'Hide password'}
      accessibilityState={{ expanded: !isHidden }}>
      <SvgIcon
        name={isHidden ? (hiddenIconName ?? 'IcEyeOff') : (iconName ?? 'IcEye')}
        size={baseSize}
        color={resolvedColor}
      />
    </Pressable>
  );
};

const useStyles = createStyles(({ scale }) => ({
  wrapper: {
    width: '100%',
  },
  containerPhone: {
    paddingLeft: scale(4),
    paddingRight: scale(8),
    height: scale(44),
  },
  containerIpad: {
    paddingHorizontal: scale(12),
    height: scale(48),
  },
  input: {
    flex: 1,
    // Let the flex:1 field actually shrink inside a narrow row. On native, yoga already
    // treats a flex child's min size as 0, but on web RN renders an <input> whose default
    // intrinsic width (its `size` attribute) acts as min-content — so in a width-constrained
    // row (e.g. the 60%-wide map search bar) the field refuses to shrink and shoves the
    // trailing clear (X) button outside the box. minWidth:0 restores the native behaviour.
    minWidth: 0,
    margin: 0,
    // Web: RN renders TextInput as an <input>, which shows the browser's default
    // blue focus outline. Remove it (design provides its own focused border state).
    // `outlineStyle` is a web-only RN style prop not present in the RN core types.
    ...(Platform.select({ web: { outlineStyle: 'none' } }) as object),
  },
  phoneField: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
  },
  phonePrefix: {
    color: colors['Labels/Primary'],
    marginRight: scale(4),
  },
  inputText: {
    color: colors['Labels/Primary'],
    textAlign: Platform.OS === 'ios' ? undefined : 'left',
    paddingVertical: 0,
  },
  iconLeft: {
    marginRight: scale(12),
  },
  iconRight: {
    marginLeft: scale(12),
  },
  label: {
    color: colors['Labels/Primary'],
  },
  helperText: {
    marginTop: scale(6),
  },
  iconWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    width: scale(24),
    height: scale(24),
  },
  flagWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: scale(24),
  },
  flagTrigger: {
    borderWidth: 0,
    backgroundColor: 'transparent',
    paddingHorizontal: 0,
    height: 'auto',
    justifyContent: 'flex-start',
    gap: scale(4),
  },
  flagChevron: {
    transform: [{ rotate: '90deg' }],
  },
  divider: {
    width: scale(1),
    height: scale(24),
    backgroundColor: colors['3'],
  },
  passwordToggle: {
    marginLeft: scale(4),
  },
  passwordTogglePressed: {
    opacity: 0.7,
  },
}));

const MemoizedRoot = memo(TextInputRoot);
const MemoizedField = memo(TextInputField);
const MemoizedLabel = memo(TextInputLabel);
const MemoizedIcon = memo(TextInputIcon);
const MemoizedFlag = memo(TextInputFlag);
const MemoizedContentContainer = memo(ContentContainer);
const MemoizedPasswordToggle = memo(TextInputPasswordToggle);
const MemoizedDivider = memo(TextInputDivider);

export const TextInput = Object.assign(MemoizedRoot, {
  Field: MemoizedField,
  Label: MemoizedLabel,
  HelperText: MemoizedHelperText,
  Icon: MemoizedIcon,
  Flag: MemoizedFlag,
  Content: MemoizedContentContainer,
  PasswordToggle: MemoizedPasswordToggle,
  Divider: MemoizedDivider,
});

export default TextInput;
